/**
 * Both prices of one order-booking ad unit, as approver-facing rows — shared by
 * the CST order drawer (`BookingCreativesAssign.js`) and the Finance Approvals
 * review (`financePanel/FinanceOrderApprovals.js`), so the two read a unit the
 * same way.
 *
 * Fed by `/slotbooking/get-order-adunits`, which returns the selling side
 * (`sales_price`, `adunit_budget`) and the rate-card side (`actual_price`,
 * `list_price`) plus `discounted`. The sales person never sees the card price
 * (the order-booking app hides it); approvers — CST, finance, sales heads — see
 * both. The unit a price is quoted in depends on how the unit is sold:
 *   - CPM: per thousand impressions;
 *   - regular CPD: per day (and the card total = per day × days);
 *   - package CPD: the package total.
 */

export const formatINR = (value) => {
  const n = Number(value);
  if (value === null || value === undefined || value === "" || Number.isNaN(n)) return null;
  return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

const round2 = (value) => Math.round(value * 100) / 100;

/**
 * `[{ key, label, value }]` for one ad unit — the same four rows the
 * order-booking app's approver view shows (its `unitPriceRows`):
 *   - CPD: per-day card rate, overall card rate, per-day sales price, overall
 *     sales price. A package's card price is its total, so per day = total ÷ days.
 *   - CPM: card and sales rate per 1,000 impressions; the overall card rate is
 *     the same impressions at the card rate (budget × card ÷ sold rate).
 * Rows with no value are left out.
 */
export const adunitPriceRows = (au) => {
  const num = (v) => (v !== null && v !== undefined && v !== "" ? Number(v) : null);
  const days = Number(au.days) || 0;
  const sales = num(au.sales_price);
  const budget = num(au.adunit_budget);
  const card = num(au.actual_price);

  const rows =
    au.campaign_type === "CPM"
      ? [
          { key: "card-rate", label: "Card Rate (per 1,000 imp.)", value: card },
          { key: "card-total", label: "Overall Card Rate", value: card != null && sales > 0 && budget != null ? round2((budget * card) / sales) : null },
          { key: "sales-rate", label: "Sales Price (per 1,000 imp.)", value: sales },
          { key: "sales-total", label: "Overall Sales Price", value: budget },
        ]
      : [
          { key: "card-rate", label: "Per-day Card Rate", value: card == null ? null : au.is_package ? (days > 0 ? round2(card / days) : null) : card },
          { key: "card-total", label: "Overall Card Rate", value: card == null ? null : au.is_package ? card : num(au.list_price) ?? card * days },
          { key: "sales-rate", label: "Per-day Sales Price", value: sales },
          { key: "sales-total", label: "Overall Sales Price", value: budget },
        ];

  return rows.filter((r) => r.value !== null && Number.isFinite(r.value)).map((r) => ({ ...r, value: formatINR(r.value) }));
};
